import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { payrollAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { cn, formatIndianCurrency } from '../../lib/utils';
import {
  ArrowLeft, Loader2, Play, Lock, Download, FileText, Plus,
  Trash2, Users, Banknote, TrendingDown, ShieldCheck, X,
} from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PROCESSED: 'bg-blue-100 text-blue-800',
  LOCKED: 'bg-green-100 text-green-800',
};

// Standard component codes that are NOT user-added adhoc
const STANDARD_CODES = new Set(['BASIC', 'HRA', 'SPA', 'TA', 'LTA', 'UA', 'CEA', 'SA', 'PF', 'ESI', 'PT', 'TDS', 'ADV', 'ARR']);

const monthLabel = (m) => MONTHS.find((x) => x.value === m)?.label ?? '';

export default function PayrollDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');   // 'process' | 'lock' | ''

  // ── Dialogs ──────────────────────────────────────────────────────────────
  const [lockOpen, setLockOpen] = useState(false);
  const [locking, setLocking] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Adhoc dialog
  const [adhocDialog, setAdhocDialog] = useState({ open: false, emp: null });
  const [adhocForm, setAdhocForm] = useState({
    component_type: 'earning',
    component_name: '',
    component_code: '',
    amount: '',
    is_recurring: false,
    recurring_to_month: new Date().getMonth() + 1,
    recurring_to_year: new Date().getFullYear(),
  });
  const [adhocSaving, setAdhocSaving] = useState(false);

  // Component detail modal
  const [componentDetail, setComponentDetail] = useState(null); // { type, name, code, amount, emp }


  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const [runRes, empRes] = await Promise.all([
        payrollAPI.getRun(runId),
        payrollAPI.getRunEmployees(runId),
      ]);
      setRun(runRes.data);
      setEmployees(empRes.data);
    } catch {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleProcess = async () => {
    setActionLoading('process');
    try {
      const res = await payrollAPI.processRun(runId);
      toast.success(`Payroll processed for ${res.data.employee_count} employees`);
      fetchData();
    } catch {
      toast.error('Failed to process payroll');
    } finally {
      setActionLoading('');
    }
  };

  const handleLockConfirm = async () => {
    setLocking(true);
    try {
      const res = await payrollAPI.lockRun(runId);
      const { emails_sent, emails_failed } = res.data;
      toast.success(`Payroll locked. Emails sent: ${emails_sent ?? 0}, Failed: ${emails_failed ?? 0}`);
      setLockOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to lock payroll');
    } finally {
      setLocking(false);
    }
  };

  const handleDownloadBankLetter = async () => {
    try {
      const res = await payrollAPI.downloadBankLetter(runId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `BankLetter-${monthLabel(run.month)}-${run.year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error('Failed to download bank letter');
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await payrollAPI.deleteRun(runId);
      toast.success('Payroll run deleted');
      navigate('/payroll');
    } catch {
      toast.error('Failed to delete payroll run');
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleDownloadPayslip = async (emp) => {
    try {
      const res = await payrollAPI.downloadPayslipPdf(emp.employee_id, runId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip-${emp.employee_code}-${monthLabel(run.month)}-${run.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error('Failed to download payslip');
    }
  };

  // ── Adhoc handlers ────────────────────────────────────────────────────────
  const openAdhocDialog = (emp) => {
    setAdhocForm({
      component_type: 'earning',
      component_name: '',
      component_code: '',
      amount: '',
      is_recurring: false,
      recurring_to_month: new Date().getMonth() + 1,
      recurring_to_year: new Date().getFullYear(),
    });
    setAdhocDialog({ open: true, emp });
  };

  const handleAdhocSave = async () => {
    const amount = parseFloat(adhocForm.amount);

    if (adhocForm.component_type === 'tds_override') {
      if (!amount || amount < 0) { toast.error('Enter a valid TDS amount'); return; }
      setAdhocSaving(true);
      try {
        await payrollAPI.setTaxOverride(runId, adhocDialog.emp.employee_id, {
          tax_override: amount,
          is_recurring: adhocForm.is_recurring,
          recurring_to_month: adhocForm.is_recurring ? adhocForm.recurring_to_month : null,
          recurring_to_year: adhocForm.is_recurring ? adhocForm.recurring_to_year : null,
        });
        toast.success('TDS override saved');
        setAdhocDialog({ open: false, emp: null });
        fetchData();
      } catch { toast.error('Failed to save TDS override'); }
      finally { setAdhocSaving(false); }
      return;
    }

    if (!adhocForm.component_name.trim()) { toast.error('Component name is required'); return; }
    if (!adhocForm.component_code.trim()) { toast.error('Component code is required'); return; }
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }

    setAdhocSaving(true);
    try {
      await payrollAPI.addAdhoc(runId, adhocDialog.emp.employee_id, {
        ...adhocForm,
        amount,
        component_code: adhocForm.component_code.toUpperCase().replace(/\s+/g, '_'),
      });
      toast.success('Component added');
      setAdhocDialog({ open: false, emp: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add component');
    } finally {
      setAdhocSaving(false);
    }
  };

  const handleAdhocRemove = async (emp, code) => {
    try {
      await payrollAPI.removeAdhoc(runId, emp.employee_id, code);
      toast.success('Component removed');
      fetchData();
    } catch {
      toast.error('Failed to remove component');
    }
  };

  // ── Tax override clear (from chip X button) ──────────────────────────────
  const handleTaxClear = async (emp) => {
    try {
      await payrollAPI.setTaxOverride(runId, emp.employee_id, { tax_override: null });
      toast.success('TDS override cleared');
      fetchData();
    } catch {
      toast.error('Failed to clear TDS override');
    }
  };


  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-muted-foreground">
        Payroll run not found.
      </div>
    );
  }

  const isLocked = run.status === 'LOCKED';
  const isProcessed = run.status === 'PROCESSED';
  const isDraft = run.status === 'DRAFT';
  const canEdit = isProcessed; // adhoc / tax override only on PROCESSED

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/payroll')} className="mt-0.5 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                {monthLabel(run.month)} {run.year} Payroll
              </h1>
              <Badge className={STATUS_STYLES[run.status]}>{run.status}</Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-0.5">
              {run.employee_count > 0
                ? `${run.employee_count} employees · Pay period: ${run.pay_period_start} to ${run.pay_period_end}`
                : 'No payroll run yet'}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
          {(isDraft || isProcessed) && (
            <Button variant="outline" size="sm" onClick={handleProcess} disabled={actionLoading === 'process'}>
              {actionLoading === 'process'
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Play className="h-4 w-4 mr-1.5" />}
              {isDraft ? 'Run Payroll' : 'Re-run'}
            </Button>
          )}
          {isProcessed && (
            <Button variant="outline" size="sm" onClick={() => setLockOpen(true)}>
              <Lock className="h-4 w-4 mr-1.5" />
              Lock Payroll
            </Button>
          )}
          {(isProcessed || isLocked) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={handleDownloadBankLetter}>
                    <FileText className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download Bank Letter</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setDeleteOpen(true)} 
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-1"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete Payroll Run</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Summary cards */}
      {run.employee_count > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Employees', value: run.employee_count, icon: Users, fmt: false },
            { label: 'Total Gross', value: run.total_gross, icon: Banknote, fmt: true },
            { label: 'Total Deductions', value: run.total_deductions, icon: TrendingDown, fmt: true },
            { label: 'Total Net Pay', value: run.total_net, icon: ShieldCheck, fmt: true },
          ].map(({ label, value, icon: Icon, fmt }) => (
            <Card key={label}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Icon className="h-4 w-4" />
                  {label}
                </div>
                <p className="text-xl font-bold">
                  {fmt ? formatIndianCurrency(value) : value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Employee table */}
      {isDraft && employees.length === 0 ? (
        <div className="border rounded-xl py-20 flex flex-col items-center gap-3 text-muted-foreground">
          <Play className="h-10 w-10 opacity-20" />
          <p>Click <strong>Run Payroll</strong> to calculate salaries for all active employees.</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead className="text-right">LOP</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => {
                  const customEarnings = (emp.other_earnings || []).filter((e) => !STANDARD_CODES.has(e.code));
                  const customDeductions = (emp.other_deductions || []).filter((d) => !STANDARD_CODES.has(d.code));
                  return (
                    <Fragment key={emp.id}>
                      <TableRow key={emp.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{emp.employee_name}</p>
                            <p className="text-xs text-muted-foreground">{emp.employee_code}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{emp.department || '—'}</TableCell>
                        <TableCell className="text-right">{formatIndianCurrency(emp.gross_salary)}</TableCell>
                        <TableCell className="text-right">{formatIndianCurrency(emp.total_deductions)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatIndianCurrency(emp.net_salary)}</TableCell>
                        <TableCell className="text-right text-sm">{emp.lop_days}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {canEdit && (
                              <>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAdhocDialog(emp)}>
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Add Adhoc Component</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            )}
                            {(isProcessed || isLocked) && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadPayslip(emp)}>
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Download Payslip</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Custom components chips */}
                      {(customEarnings.length > 0 || customDeductions.length > 0 || emp.tax_override != null) && (
                        <TableRow key={`${emp.id}-custom`} className="bg-slate-50/50">
                          <TableCell colSpan={7} className="py-1.5 px-4">
                            <div className="flex flex-wrap gap-1.5">
                              {customEarnings.map((e) => (
                                <span key={e.code} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
                                  <button className="hover:underline" onClick={() => setComponentDetail({ kind: 'earning', ...e, emp })}>
                                    + {e.name}: {formatIndianCurrency(e.amount)}
                                  </button>
                                  {canEdit && (
                                    <button onClick={() => handleAdhocRemove(emp, e.code)} className="ml-0.5 hover:text-red-500">
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </span>
                              ))}
                              {customDeductions.map((d) => (
                                <span key={d.code} className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
                                  <button className="hover:underline" onClick={() => setComponentDetail({ kind: 'deduction', ...d, emp })}>
                                    − {d.name}: {formatIndianCurrency(d.amount)}
                                  </button>
                                  {canEdit && (
                                    <button onClick={() => handleAdhocRemove(emp, d.code)} className="ml-0.5 hover:text-red-500">
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </span>
                              ))}
                              {emp.tax_override != null && (
                                <span className="inline-flex items-center gap-1 text-xs bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5">
                                  <button className="hover:underline" onClick={() => setComponentDetail({ kind: 'tds_override', name: 'TDS Override', amount: emp.tax_override, tds: emp.tds, emp })}>
                                    TDS Override: {formatIndianCurrency(emp.tax_override)}
                                  </button>
                                  {canEdit && (
                                    <button onClick={() => handleTaxClear(emp)} className="ml-0.5 hover:text-red-500">
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Lock Dialog ── */}
      <Dialog open={lockOpen} onOpenChange={(o) => !locking && setLockOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock Payroll</DialogTitle>
            <DialogDescription>
              This will lock {monthLabel(run.month)} {run.year} payroll and send payslip emails to all employees.
              This action <strong>cannot be undone</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockOpen(false)} disabled={locking}>Cancel</Button>
            <Button variant="destructive" onClick={handleLockConfirm} disabled={locking}>
              {locking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {locking ? 'Sending Emails…' : 'Lock & Send Emails'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteOpen} onOpenChange={(o) => !deleting && setDeleteOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Payroll Run</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the <strong>{monthLabel(run.month)} {run.year}</strong> payroll run? 
              This will permanently remove all calculated salaries and records for this period.
              {isLocked && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100 text-red-800 text-xs space-y-2">
                  <p className="font-bold">Important for Locked Payroll:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Any salary advance repayments made in this run will be <strong>reverted</strong>.</li>
                    <li>Payslip emails already sent to employees cannot be recalled.</li>
                    <li>Ensure you haven't already processed bank transfers for this run.</li>
                  </ul>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {deleting ? 'Deleting...' : 'Yes, Delete Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Adhoc Dialog ── */}
      <Dialog open={adhocDialog.open} onOpenChange={(o) => !adhocSaving && setAdhocDialog({ ...adhocDialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Adhoc Component</DialogTitle>
            <DialogDescription>
              Add a one-time or recurring earning / deduction for{' '}
              <strong>{adhocDialog.emp?.employee_name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select
                  value={adhocForm.component_type}
                  onValueChange={(v) => setAdhocForm({ ...adhocForm, component_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="earning">Earning</SelectItem>
                    <SelectItem value="deduction">Deduction</SelectItem>
                    <SelectItem value="tds_override">TDS Override</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {adhocForm.component_type !== 'tds_override' && (
                <div className="space-y-1.5">
                  <Label>Component Code</Label>
                  <Input
                    placeholder="e.g. BONUS"
                    value={adhocForm.component_code}
                    onChange={(e) => setAdhocForm({ ...adhocForm, component_code: e.target.value })}
                  />
                </div>
              )}
            </div>
            {adhocForm.component_type === 'tds_override' ? (
              <>
                <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">System-calculated TDS</span>
                    <span className="font-medium">{formatIndianCurrency(adhocDialog.emp?.tds ?? 0)}</span>
                  </div>
                  {adhocDialog.emp?.tax_override != null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Current override</span>
                      <span className="font-medium text-orange-600">{formatIndianCurrency(adhocDialog.emp.tax_override)}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Override Amount (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder={String(adhocDialog.emp?.tds ?? 0)}
                    value={adhocForm.amount}
                    onChange={(e) => setAdhocForm({ ...adhocForm, amount: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Frequency</Label>
                  <Select
                    value={adhocForm.is_recurring ? 'recurring' : 'onetime'}
                    onValueChange={(v) => setAdhocForm({ ...adhocForm, is_recurring: v === 'recurring' })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="onetime">One-time (this month only)</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {adhocForm.is_recurring && (
                  <div className="space-y-1.5">
                    <Label>Apply until</Label>
                    <div className="flex gap-2">
                      <Select
                        value={String(adhocForm.recurring_to_month)}
                        onValueChange={(v) => setAdhocForm({ ...adhocForm, recurring_to_month: Number(v) })}
                      >
                        <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        className="w-24"
                        value={adhocForm.recurring_to_year}
                        onChange={(e) => setAdhocForm({ ...adhocForm, recurring_to_year: Number(e.target.value) })}
                        min={2024} max={2035}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>Component Name</Label>
                  <Input
                    placeholder="e.g. Performance Bonus"
                    value={adhocForm.component_name}
                    onChange={(e) => setAdhocForm({ ...adhocForm, component_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="0.00"
                    value={adhocForm.amount}
                    onChange={(e) => setAdhocForm({ ...adhocForm, amount: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Frequency</Label>
                  <Select
                    value={adhocForm.is_recurring ? 'recurring' : 'onetime'}
                    onValueChange={(v) => setAdhocForm({ ...adhocForm, is_recurring: v === 'recurring' })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="onetime">One-time (this month only)</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {adhocForm.is_recurring && (
                  <div className="space-y-1.5">
                    <Label>Apply until</Label>
                    <div className="flex gap-2">
                      <Select
                        value={String(adhocForm.recurring_to_month)}
                        onValueChange={(v) => setAdhocForm({ ...adhocForm, recurring_to_month: Number(v) })}
                      >
                        <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        className="w-24"
                        value={adhocForm.recurring_to_year}
                        onChange={(e) => setAdhocForm({ ...adhocForm, recurring_to_year: Number(e.target.value) })}
                        min={2024} max={2035}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdhocDialog({ open: false, emp: null })} disabled={adhocSaving}>
              Cancel
            </Button>
            <Button onClick={handleAdhocSave} disabled={adhocSaving}>
              {adhocSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Component Detail Modal ── */}
      <Dialog open={!!componentDetail} onOpenChange={(o) => !o && setComponentDetail(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Component Details</DialogTitle>
            <DialogDescription>{componentDetail?.emp?.employee_name}</DialogDescription>
          </DialogHeader>
          {componentDetail && (
            <div className="space-y-3 py-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Type</span>
                <span className={`font-medium capitalize ${
                  componentDetail.kind === 'earning' ? 'text-green-700' :
                  componentDetail.kind === 'deduction' ? 'text-red-700' : 'text-orange-700'
                }`}>
                  {componentDetail.kind === 'tds_override' ? 'TDS Override' : componentDetail.kind}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Name</span>
                <span className="font-medium">{componentDetail.name}</span>
              </div>
              {componentDetail.code && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Code</span>
                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{componentDetail.code}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-semibold">{formatIndianCurrency(componentDetail.amount)}</span>
              </div>
              {componentDetail.kind === 'tds_override' && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">System TDS</span>
                  <span className="font-medium text-muted-foreground">{formatIndianCurrency(componentDetail.tds)}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setComponentDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {false && <Dialog>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Salary Arrears</DialogTitle>
            <DialogDescription>
              Calculate and apply arrears to <strong>{monthLabel(run.month)} {run.year}</strong> payroll.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Period */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Arrears Period</Label>
              <div className="grid grid-cols-2 gap-6">
                {[['From', 'from_month', 'from_year'], ['To', 'to_month', 'to_year']].map(([lbl, mk, yk]) => (
                  <div key={lbl} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{lbl}</Label>
                    <div className="flex gap-2">
                      <Select
                        value={String(arrearsForm[mk])}
                        onValueChange={(v) => setArrearsForm({ ...arrearsForm, [mk]: Number(v) })}
                      >
                        <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        className="w-24"
                        value={arrearsForm[yk]}
                        onChange={(e) => setArrearsForm({ ...arrearsForm, [yk]: Number(e.target.value) })}
                        min={2020} max={2035}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Employee picker */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Employees</Label>
                {selectedEmpIds.length > 0 && (
                  <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelectedEmpIds([])}>
                    Clear all
                  </button>
                )}
              </div>
              <Popover open={empPickerOpen} onOpenChange={setEmpPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    {selectedEmpIds.length === 0
                      ? 'Select employees…'
                      : `${selectedEmpIds.length} employee${selectedEmpIds.length > 1 ? 's' : ''} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search…" />
                    <CommandList>
                      <CommandEmpty>No employees found.</CommandEmpty>
                      <CommandGroup>
                        {arrearsEmployees.map((emp) => {
                          const sel = selectedEmpIds.includes(emp.id);
                          return (
                            <CommandItem
                              key={emp.id}
                              value={`${emp.first_name} ${emp.last_name} ${emp.employee_code}`}
                              onSelect={() => setSelectedEmpIds((p) => sel ? p.filter((id) => id !== emp.id) : [...p, emp.id])}
                            >
                              <Check className={cn('mr-2 h-4 w-4', sel ? 'opacity-100' : 'opacity-0')} />
                              {emp.first_name} {emp.last_name}
                              <span className="ml-2 text-xs text-muted-foreground">{emp.employee_code}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedEmpIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {selectedEmpIds.map((id) => {
                    const e = arrearsEmployees.find((x) => x.id === id);
                    if (!e) return null;
                    return (
                      <Badge key={id} variant="secondary" className="gap-1 pr-1">
                        {e.first_name} {e.last_name}
                        <button onClick={() => setSelectedEmpIds((p) => p.filter((i) => i !== id))}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            <Button variant="outline" onClick={handleCalculateArrears} disabled={arrearsCalculating || selectedEmpIds.length === 0}>
              {arrearsCalculating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Calculate
            </Button>
          </div>

          {/* Preview table */}
          {arrearsPreview.length > 0 && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Preview</Label>
                <p className="text-xs text-muted-foreground">
                  Total: <strong>{formatIndianCurrency(arrearsPreview.reduce((s, r) => s + (parseFloat(r.override) || 0), 0))}</strong>
                </p>
              </div>
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Old Gross/mo</TableHead>
                      <TableHead className="text-right">New Gross/mo</TableHead>
                      <TableHead className="text-right">Months</TableHead>
                      <TableHead className="text-right">Calculated</TableHead>
                      <TableHead className="text-right">Amount to Pay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {arrearsPreview.map((row) => (
                      <TableRow key={row.employee_id}>
                        <TableCell>
                          <p className="font-medium text-sm">{row.employee_name}</p>
                          <p className="text-xs text-muted-foreground">{row.employee_code}</p>
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatIndianCurrency(row.old_monthly_gross)}</TableCell>
                        <TableCell className="text-right text-sm">{formatIndianCurrency(row.new_monthly_gross)}</TableCell>
                        <TableCell className="text-right text-sm">{row.months_count}</TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">{formatIndianCurrency(row.arrears_amount)}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            className="w-32 text-right ml-auto"
                            value={row.override}
                            min={0}
                            onChange={(e) =>
                              setArrearsPreview((prev) =>
                                prev.map((r) => r.employee_id === row.employee_id ? { ...r, override: e.target.value } : r)
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

        </DialogContent>
      </Dialog>}
    </div>
  );
}
